import React, { FC, useContext, useEffect } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import useFormPersist from 'react-hook-form-persist';
import { useHistory } from 'react-router-dom';
import { ALL_TEST_CODES, CATEGORY_DEFINITIONS } from '../../constants/test-categories';
// import { MAX_DOCUMENT_LENGTH } from '../../constants/common'; // deactivated Attest
import { useTranslation } from '../../hooks/useTranslation';
import { MainLocales, PriceLocales } from '../../locales/types';
import { BookingStep } from '../../routes/BookingCalendlyRedirectPage/enums/booking-step.enum';
import { BookingCalendlyRedirectInputsSchema } from '../../routes/BookingCalendlyRedirectPage/validation';
import { HomePageDialog } from '../../routes/HomePage/HomePageDialog/HomePageDialog';
import { store } from '../../store/store';
import { Cost, Patient, TiranCostPayload } from '../../types/api-types';
// import { DocumentType } from '../../types/enums/document-type.enum'; // deactivated Attest
import { HomePageDialogState } from '../../types/enums/home-page-dialog-state.enum';
import { Path } from '../../types/enums/path.enum';
import { UserVerificationState } from '../../types/enums/user-verification-state.enum';
import { getUserVerificationState } from '../../utils/auth';
import { hideHomePageDialog, showHomePageDialog } from '../../utils/home-page-dialog-state-handlers';
import { Price } from '../Price/Price';
import { Button } from '../ui/Button/Button';
import { Checkbox } from '../ui/Checkbox/Checkbox';
import { Input } from '../ui/Input/Input';
import style from './BookingOptionalExtras.module.scss';

interface Props {
  costs: Cost;
  patient?: Patient;
  preSelection?: boolean;
  setBookingStep: (step: BookingStep) => void;
  visible?: boolean;
}

type CostsObj = Omit<TiranCostPayload, 'sameDayService' | 'englishResults' | 'invoice'>;

function getSmallestPositiveValue(obj: CostsObj): number {
  const positiveValues = Object.values(obj).filter(value => value > 0);

  if (positiveValues.length === 0) {
    return 0; // No positive values found
  }

  // Find the minimum positive value
  return Math.min(...positiveValues);
}

export const BookingOptionalExtras: FC<Props> = ({ costs, patient, preSelection, visible }) => {
  const { state, dispatch } = useContext(store);
  const { push } = useHistory();
  const { t } = useTranslation<MainLocales>('mainLocales');
  const { t: tPrice } = useTranslation<PriceLocales>('priceLocales');
  const {
    control,
    errors,
    formState,
    setValue,
    getValues,
    watch,
    setError ,
    clearErrors
  } = useFormContext<BookingCalendlyRedirectInputsSchema>();
  const watchCovidABTest = watch('covidABTest');
  // const watchCovidIgMTest = watch('covidIgMTest'); // Additional test removed
  // const watchCovidPCRTest = watch('covidPCRTest'); // PCR deactivated
  // const watchEnglishResults = watch('englishResults'); // Attest deactivated
  // const watchSameDayService = watch('sameDayService'); // PCR is now always same-day service
  const watchO_DROG = watch('O_DROG');
  const watchO_CDT = watch('O_CDT');
  const watchO_CDTPRO = watch('O_CDTPRO');
  const watchO_HIV = watch('O_HIV');
  const watchO_HEPC = watch('O_HEPC');
  const watchO_DAO = watch('O_DAO');
  const watchO_OMEGA = watch('O_OMEGA');
  const watchO_FSMEIGG = watch('O_FSMEIGG');
  const watchO_HAVAK = watch('O_HAVAK');
  const watchO_HBSAK = watch('O_HBSAK');
  const watchO_MASERN = watch('O_MASERN');
  const watchO_MUMPS = watch('O_MUMPS');
  const watchO_ROET = watch('O_ROET');
  const watchO_VZV = watch('O_VZV');
  const watchO_VITA = watch('O_VITA');
  const watchO_VITB1 = watch('O_VITB1');
  const watchO_VITB2 = watch('O_VITB2');
  const watchO_VITB3 = watch('O_VITB3');
  const watchO_VB6 = watch('O_VB6');
  const watchO_FOL = watch('O_FOL');
  const watchO_HOLOTC = watch('O_HOLOTC');
  const watchO_VB12 = watch('O_VB12');
  const watchO_VITC = watch('O_VITC');
  const watchO_VITD = watch('O_VITD');
  const watchO_VD2 = watch('O_VD2');
  const watchO_VITE = watch('O_VITE');
  const watchO_VITK1 = watch('O_VITK1');
  const watchO_VITK2 = watch('O_VITK2');
  const watchO_UBI = watch('O_UBI');
  const watchO_NAV = watch('O_NAV');
  const watchO_KV = watch('O_KV');
  const watchO_CAV = watch('O_CAV');
  const watchO_MGV = watch('O_MGV');
  const watchO_FEV = watch('O_FEV');
  const watchO_SEV = watch('O_SEV');
  const watchO_ZNV = watch('O_ZNV');
  const watchO_CUV = watch('O_CUV');
  // const watchO_MINK = watch('O_MINK'); // test panel removed
  // const watchO_MINM = watch('O_MINM'); // test panel removed
  // const watchO_MINVB = watch('O_MINVB'); // test panel removed
  const watchO_VMULTIK = watch('O_VMULTIK');
  const watchO_VMULTIP = watch('O_VMULTIP');
  const watchO_SMULTIK = watch('O_SMULTIK');
  const watchO_SMULTIP = watch('O_SMULTIP');
  const watchO_HMULTIK = watch('O_HMULTIK');
  const watchO_HMULTIP = watch('O_HMULTIP');
  const watchO_VU = watch('O_VU');
  const watchO_VEGGIE = watch('O_VEGGIE');
  const watchO_SPORTB = watch('O_SPORTB');
  const watchO_SPORTPLUS = watch('O_SPORTPLUS');
  const watchO_VITBAS = watch('O_VITBAS');
  const watchO_VITPLUS = watch('O_VITPLUS');
  const watchO_HAARE = watch('O_HAARE');
  // const watchO_RESPI = watch('O_RESPI'); // test panel removed
  // const watchO_COLAIQ = watch('O_COLAIQ');
  const watchO_STI = watch('O_STI');
  const watchO_STIPLUS = watch('O_STIPLUS');
  const watchInvoice = watch('invoice');
  // This must be in sessionStorage to persist past a Calendly redirect or a page refresh
  const firstOpening = !!window.sessionStorage.getItem('invPreSel');
  // @ts-ignore
  const costsPayload: TiranCostPayload = costs.payload;
  const { covidTest, ...restCosts } = costsPayload;
  const { sameDayService, englishResults, invoice, ...testCosts } = costsPayload;
  const totalPrice = Object.entries(restCosts).reduce(
    (prev, [key, value]) => (getValues()[key as keyof BookingCalendlyRedirectInputsSchema] ? prev + value : prev),
    0,
  );
  const minTestCost = getSmallestPositiveValue(testCosts);
  const watchBloodDraw = watchO_CDT ||
    watchO_CDTPRO ||
    watchO_HIV ||
    watchO_HEPC ||
    watchO_DAO ||
    watchO_OMEGA ||
    watchO_FSMEIGG ||
    watchO_HAVAK ||
    watchO_HBSAK ||
    watchO_MASERN ||
    watchO_MUMPS ||
    watchO_ROET ||
    watchO_VZV ||
    watchO_VITA ||
    watchO_VITB1 ||
    watchO_VITB2 ||
    watchO_VITB3 ||
    watchO_VB6 ||
    watchO_FOL ||
    watchO_HOLOTC ||
    watchO_VB12 ||
    watchO_VITC ||
    watchO_VITD ||
    watchO_VD2 ||
    watchO_VITE ||
    watchO_VITK1 ||
    watchO_VITK2 ||
    watchO_UBI ||
    watchO_NAV ||
    watchO_KV ||
    watchO_CAV ||
    watchO_MGV ||
    watchO_FEV ||
    watchO_SEV ||
    watchO_ZNV ||
    watchO_CUV ||
    // watchO_MINK ||
    // watchO_MINM ||
    // watchO_MINVB ||
    watchO_VMULTIK ||
    watchO_VMULTIP ||
    watchO_SMULTIK ||
    watchO_SMULTIP ||
    watchO_VU ||
    watchO_VEGGIE ||
    watchO_SPORTB ||
    watchO_SPORTPLUS ||
    watchO_VITBAS ||
    watchO_VITPLUS ||
    watchO_HAARE ||
    watchO_STI ||
    watchO_STIPLUS;
  const allTestCodes = ALL_TEST_CODES;

  const category1Checkboxes = CATEGORY_DEFINITIONS[0].items.map(item => item.name);
  const category2Checkboxes = CATEGORY_DEFINITIONS[1].items.map(item => item.name);
  const category3Checkboxes = CATEGORY_DEFINITIONS[2].items.map(item => item.name);
  const category4Checkboxes = CATEGORY_DEFINITIONS[3].items.map(item => item.name);
  const category5Checkboxes = CATEGORY_DEFINITIONS[4].items.map(item => item.name);

  const labelKeysByCode = CATEGORY_DEFINITIONS.reduce<Record<string, { booking: string; price?: string }>>(
    (acc, category) => {
      category.items.forEach(item => {
        acc[item.name] = { booking: item.bookingLabelKey, price: item.priceLabelKey };
      });

      return acc;
    },
    {}
  );

  const getPriceLabel = (code: string) => {
    const keys = labelKeysByCode[code];
    const priceKey = keys?.price;

    if (priceKey) {
      const priceText = tPrice(priceKey as keyof PriceLocales);
      if (priceText !== priceKey) {
        return priceText;
      }
    }

    return t(keys.booking as keyof MainLocales);
  };
  // const category1Values = category1Checkboxes.map(name => watch(name));
  // const isAnyCategory1Selected = category1Values.some((value: boolean) => value);
  // // Use watch for reactive checkbox values
  // const category1CheckedValues = useWatch({
  //   control,
  //   name: category1Checkboxes,
  // });
  // // Function to validate only one checkbox selected from the category
  // const validateOnlyOneSelected = () => {
  //   const values = getValues(category1Checkboxes);
  //   const selected = Object.values(values).filter(Boolean); // Filters out unchecked boxes
  //
  //   if (selected.length > 1) {
  //     setError('category1Validation', {
  //       type: 'manual',
  //       message: 'Only one option can be selected from this group.',
  //     });
  //   } else {
  //     clearErrors('category1Validation');
  //   }
  // };
  //
  // // Handle pre-selecting checkboxes via hash link
  // useEffect(() => {
  //   const hash = window.location.hash.substring(1);
  //
  //   if (category1Checkboxes.includes(hash)) {
  //     setValue(hash, true, { shouldValidate: true });
  //     validateOnlyOneSelected(); // 🔥 Trigger validation after pre-selecting via hash
  //   }
  // }, [setValue]);
  //
  // // Validate when category1CheckedValues change (checkbox selection state)
  // useEffect(() => {
  //   validateOnlyOneSelected(); // Trigger the validation when the values change
  // }, [category1CheckedValues, setError, clearErrors, getValues]);

  // Use watch for reactive checkbox values (this returns an object)
  const category1CheckedValues = useWatch({
    control,
    name: category1Checkboxes,
  });

  // Function to validate only one checkbox selected from the category
  const validateOnlyOneSelected = () => {
    const values = getValues(category1Checkboxes);
    const selected = Object.values(values).filter(Boolean); // Filters out unchecked boxes

    if (selected.length > 1) {
      setError('category1Validation', {
        type: 'manual',
        message: t('bookingFormTest_1_x_error'),
      });
    } else {
      clearErrors('category1Validation');
    }
  };

  // Handle pre-selecting checkboxes via hash link
  // useEffect(() => {
  //   const hash = window.location.hash.substring(1);
  //
  //   if (category1Checkboxes.includes(hash)) {
  //     setValue(hash, true, { shouldValidate: true });
  //     validateOnlyOneSelected(); // 🔥 Trigger validation after pre-selecting via hash
  //   }
  // }, [setValue]);

  // Validate when category1CheckedValues change (checkbox selection state)
  useEffect(() => {
    // Convert category1CheckedValues object to array and check if any checkbox is selected
    const selectedCount = Object.values(category1CheckedValues).filter(Boolean).length;

    if (selectedCount > 1) {
      setError('category1Validation', {
        type: 'manual',
        message: t('bookingFormTest_1_x_error'),
      });
    } else {
      clearErrors('category1Validation');
    }
  }, [category1CheckedValues, setError, clearErrors, getValues, t]);

  const isAnyCategory1Selected = Object.values(category1CheckedValues).some(Boolean);
  useEffect(() => {
    setValue('bloodDraw', watchBloodDraw);
  }, [setValue, watchBloodDraw]);

  // TODO consider moving the next two to BookingProductSelectionPageContainer
  const handleHomeDialogClose = () => hideHomePageDialog(dispatch);

  const handleAppointmentClick = () => {
    // TODO consider adding validation here
    if (state.currentUser) {
      switch (getUserVerificationState(state.currentUser!)) {
        case UserVerificationState.VERIFIED: {
          return push(Path.BOOKING);
        }
        case UserVerificationState.EMAIL_NOT_VERIFIED: {
          return showHomePageDialog(dispatch, HomePageDialogState.confirmationEmailSent);
        }
        case UserVerificationState.PHONE_NUMBER_NOT_VERIFIED: {
          if (state.currentUser?.attributes.phone_number) {
            return showHomePageDialog(dispatch, HomePageDialogState.otpConfirmation);
          }

          return showHomePageDialog(dispatch, HomePageDialogState.phoneConfirmation);
        }
      }
    } else {
      showHomePageDialog(dispatch, HomePageDialogState.loginEmailBookingFlow);
    }
  };

  // @ts-ignore
  const patientDetails = [
    'firstName',
    'lastName',
    'birthDate',
    'gender',
    'phoneNumber',
    'email',
    'sendResultsTo',
    'streetName',
    'houseNumber',
    'postalCode',
    'city',
    'country',
    'symptomFree'
  ];
  const formName = 'productOptions';

  useFormPersist(formName, { watch, setValue, exclude: patientDetails});
  const persistedValues = JSON.parse(window.sessionStorage.getItem(formName) || '{}');

  const getExpandedUuids = (selected: Record<string, unknown>) => {
    const isAnySelected = (names: string[]) => names.some(name => Boolean(selected[name]));
    const expanded: string[] = [];

    if (isAnySelected(category1Checkboxes)) { expanded.push('Category-1'); }
    if (isAnySelected(category2Checkboxes)) { expanded.push('Category-2'); }
    if (isAnySelected(category3Checkboxes)) { expanded.push('Category-3'); }
    if (isAnySelected(category4Checkboxes)) { expanded.push('Category-4'); }
    if (isAnySelected(category5Checkboxes)) { expanded.push('Category-5'); }

    return expanded.length > 0 ? expanded : ['Category-1'];
  };

  const preExpandedUuids = (() => {
    const selected = { ...persistedValues } as Record<string, unknown>;
    const hash = window.location.hash.substring(1);

    if (allTestCodes.includes(hash)) {
      selected[hash] = true;
    }

    return getExpandedUuids(selected);
  })();

  // Pre-select checkbox based on URL hash if it matches one of the names
  useEffect(() => {
    const hash = window.location.hash.substring(1); // remove the '#' from the hash
    if (allTestCodes.includes(hash)) {
      setValue(hash, true, { shouldValidate: true });
      // Remove the hash to not interfere with the rest of the program:
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [setValue, allTestCodes]);

  const Category1Link = () => (
    <a href='https://www.labor-tiran.at/patienteninformation/vorsorgeprofile-im-detail/'>
      {t('bookingFormTest_1_0_sublink')}
    </a>
  );

  const items = CATEGORY_DEFINITIONS.map(category => {
    if (category.uuid === 'Category-1') {
      return {
        uuid: category.uuid,
        rank: category.rank,
        heading: t(category.bookingHeadingKey as keyof MainLocales),
        content: (
          <div>
                <span className={style.inline}>
                  {t(category.bookingSubKey as keyof MainLocales)}&nbsp;<Category1Link />
                </span>
            {category.items.map(item => (
              <div className={style.rowSpaceBetween} key={item.name}>
                <Checkbox
                  name={item.name}
                  control={control}
                  error={errors[item.name] ? { type: 'required' } : null}
                  onChange={({ target }) => {
                    setValue(item.name, target.checked, { shouldValidate: true });
                    validateOnlyOneSelected();
                  }}
                  className={`${style.marginBottom} ${style.checkboxTop}`}
                  defaultValue={!!persistedValues[item.name]}
                  disabled={isAnyCategory1Selected && !watch(item.name)}
                >
                  <span className={style.header}>{t(item.bookingLabelKey as keyof MainLocales)}</span>
                </Checkbox>
                <Price value={costsPayload[item.name]} symbol={costs.currencySymbol} />
              </div>
            ))}
            {errors.category1Validation && (
              <div style={{ color: 'red', marginTop: '10px' }}>
                {errors.category1Validation.message}
              </div>
            )}

            {isAnyCategory1Selected && !errors.category1Validation && (
              <p className={style.rowSpaceBetween}>
                {t('bookingFormTest_1_x')}
              </p>
            )}
          </div>
        ),
      };
    }

    return {
      uuid: category.uuid,
      rank: category.rank,
      heading: t(category.bookingHeadingKey as keyof MainLocales),
      content: (
        <div>
          {category.items.map(item => (
            <div className={style.rowSpaceBetween} key={item.name}>
              <Checkbox
                name={item.name}
                control={control}
                error={errors[item.name] ? { type: 'required' } : null}
                onChange={({ target }) => {
                  setValue(item.name, target.checked, { shouldValidate: true });
                  if (category.uuid === 'Category-5') {
                    validateOnlyOneSelected();
                  }
                }}
                className={`${style.marginBottom} ${style.checkboxTop}`}
                defaultValue={!!persistedValues[item.name]}
                disabled={category.uuid === 'Category-5' && isAnyCategory1Selected && !watch(item.name)}
              >
                {(() => {
                  const label = t(item.bookingLabelKey as keyof MainLocales);
                  const [firstLine, secondLine] = label.split('\n');

                  return (
                    <span className={style.labelText}>
                          <span className={style.header}>{firstLine}</span>
                      {secondLine && <span className={style.headerSubLine}>{secondLine}</span>}
                        </span>
                  );
                })()}
              </Checkbox>
              <span className={style.priceAlign}>
                    <Price value={costsPayload[item.name]} symbol={costs.currencySymbol} />
                  </span>
            </div>
          ))}
        </div>
      ),
    };
  });

  const rankedItems = items.slice().sort((a, b) => a.rank - b.rank);

  return (
    <div style={{ width: '100%', display: visible ? undefined : 'none' }}>
      <div className={style.row}>
        <div className={style.column}>
          {/*TODO consider putting back icon back into here together with conditional as with buttons*/}
          {/*<Icon alt='<-' type='ic-back' onClick={handleBackIconClick} marginBottom />*/}
          <h1>{t('bookingFormTestTypeHeader')}</h1>
          <p>{t('bookingFormTestTypeSubheader')}</p>
          <Accordion
            allowMultipleExpanded={true}
            allowZeroExpanded={true}
            preExpanded={preExpandedUuids}
          >
            {rankedItems.map(item => (
              <AccordionItem
                key={item.uuid}
                uuid={item.uuid}
              >
                <AccordionItemHeading>
                  <AccordionItemButton>{item.heading}</AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>{item.content}</AccordionItemPanel>
              </AccordionItem>
            ))}
          </Accordion>
          <span className={style.subparagraph}><br /></span>
          <h2>{t('bookingFormOptionalExtrasHeader')}</h2>
          <p>{t('bookingFormOptionalExtrasSubheader')}</p>
          {/*REMOVE SAME DAY OPTION - NEEDS TO EXIST BUT BE HIDDEN TO WORK*/}
          {/*
          <div style={{ display: 'none' }}>
              <>
                <Checkbox
                  name='sameDayService'
                  control={control}
                  error={errors.sameDayService ? { type: 'required' } : null}
                  onChange={({ target }) => {
                    setValue('doNothing', target.checked );
                    // tslint:disable-next-line:no-console
                    // console.log(getValues());
                  }}
                  className={style.marginBottom}
                  defaultValue={persistedValues.sameDayService ? true : false}
                >
                  <span className={style.header}>{t('bookingFormSameDayService')}</span>
                </Checkbox>
                <Price value={costsPayload.sameDayService} symbol={costs.currencySymbol} sign />
              </>
          </div>
          */}
          {/* Use Controller for hidden fields */}
          <div style={{ display: 'none' }}>
            <Controller
              name='bloodDraw'
              control={control}
              defaultValue={false}
            />
          </div>
          {/*<div className={style.rowSpaceBetween}>
              {watchCovidPCRTest && (
                <>
                  <Checkbox
                    name='doctorsNote'
                    control={control}
                    error={errors.doctorsNote ? { type: 'required' } : null}
                    onChange={({ target }) => {
                      setValue('doctorsNote', target.checked, { shouldValidate: true });
                    }}
                    className={style.marginBottom}
                    defaultValue={persistedValues.doctorsNote ? true : false}
                  >
                    <span className={style.header}>{t('bookingFormDoctorsNote')}</span>
                  </Checkbox>
                  <Price value={costsPayload.englishResults} symbol={costs.currencySymbol} sign />
                </>
              )}
          </div>
          {watchCovidPCRTest && (<span className={style.subparagraph}>{DoctorsNote2')}</span>)}

          {watchDoctorsNote && !preSelection && (
            <>
              <p className={style.marginTop}>{t('bookingFormDoctorsNoteSub')}</p>
              <Input
                control={control}
                name={'documents.' + DocumentType.PASSPORT}
                label={t(DocumentType.PASSPORT)}
                className={style.marginBottom}
                error={errors.documents?.PASSPORT!}
                defaultValue={persistedValues.documents?.PASSPORT || patient?.documents?.PASSPORT || undefined}
                maxLength={MAX_DOCUMENT_LENGTH}
              />
              <Input
                control={control}
                name='nationality'
                label={t('nationality')}
                className={style.marginBottom}
                error={errors.nationality!}
                defaultValue={persistedValues.nationality || patient?.nationality || undefined }
              />
            </>
          )}*/}

          <div className={style.rowSpaceBetween}>
            <Checkbox
              name='invoice'
              control={control}
              error={errors.invoice ? { type: 'required' } : null}
              onChange={({ target }) => {
                setValue('invoice', target.checked, { shouldValidate: true });
                // Needs to be in sessionStorage to persist past Calendly redirect
                preSelection && window.sessionStorage.setItem('invPreSel', String(target.checked));
              }}
              className={`${style.marginBottom} ${style.checkboxTop}`}
              defaultValue={!!persistedValues.invoice}
            >
              <span className={style.header}>{t('bookingFormInvoiceExtrasHeader')}</span>
            </Checkbox>
            <Price value={costsPayload.invoice} symbol={costs.currencySymbol} sign />
          </div>
          {watchInvoice && !firstOpening && !preSelection && (
            <>
              <p className={style.marginTop}>{t('bookingFormInvoiceExtrasInfo')}</p>
              <Input
                control={control}
                name={'invoiceAddress.addressLine1'}
                label={t('addressLine1')}
                className={style.marginBottom}
                error={errors.invoiceAddress?.addressLine1!}
                defaultValue={persistedValues.invoiceAddress?.addressLine1 || patient?.invoiceAddress?.addressLine1 || undefined}
                required
              />
              <Input
                control={control}
                name={'invoiceAddress.addressLine2'}
                label={t('addressLine2')}
                className={style.marginBottom}
                error={errors.invoiceAddress?.addressLine2!}
                defaultValue={persistedValues.invoiceAddress?.addressLine2 || patient?.invoiceAddress?.addressLine2 || undefined}
                required
              />
              <Input
                control={control}
                name={'invoiceAddress.addressLine3'}
                label={t('addressLine3')}
                className={style.marginBottom}
                error={errors.invoiceAddress?.addressLine3!}
                defaultValue={persistedValues.invoiceAddress?.addressLine3 || patient?.invoiceAddress?.addressLine3 || undefined}
              />
              <Input
                control={control}
                name={'invoiceAddress.addressLine4'}
                label={t('addressLine4')}
                className={style.marginBottom}
                error={errors.invoiceAddress?.addressLine4!}
                defaultValue={persistedValues.invoiceAddress?.addressLine4 || patient?.invoiceAddress?.addressLine4 || undefined}
              />
              <Input
                control={control}
                name={'invoiceAddress.addressLine5'}
                label={t('addressLine5')}
                className={style.marginBottom}
                error={errors.invoiceAddress?.addressLine5!}
                defaultValue={persistedValues.invoiceAddress?.addressLine5 || patient?.invoiceAddress?.addressLine5 || undefined}
              />
            </>
          )}
        </div>
        <div className={style.vline} />
        <div className={style.column}>
          <h1 className={style.mobileTopSpace}>{t('bookingFormBillingInfoHeader')}</h1>
          <div className={style.priceLines}>
            {[
              { condition: watchO_VU, label: getPriceLabel('O_VU'), value: costsPayload.O_VU },
              { condition: watchO_VEGGIE, label: getPriceLabel('O_VEGGIE'), value: costsPayload.O_VEGGIE },
              { condition: watchO_SPORTB, label: getPriceLabel('O_SPORTB'), value: costsPayload.O_SPORTB },
              { condition: watchO_SPORTPLUS, label: getPriceLabel('O_SPORTPLUS'), value: costsPayload.O_SPORTPLUS },
              { condition: watchO_VITBAS, label: getPriceLabel('O_VITBAS'), value: costsPayload.O_VITBAS },
              { condition: watchO_VITPLUS, label: getPriceLabel('O_VITPLUS'), value: costsPayload.O_VITPLUS },
              { condition: watchO_HAARE, label: getPriceLabel('O_HAARE'), value: costsPayload.O_HAARE },
              { condition: watchO_STI, label: getPriceLabel('O_STI'), value: costsPayload.O_STI },
              { condition: watchO_STIPLUS, label: getPriceLabel('O_STIPLUS'), value: costsPayload.O_STIPLUS },
              { condition: watchO_FSMEIGG, label: getPriceLabel('O_FSMEIGG'), value: costsPayload.O_FSMEIGG },
              { condition: watchO_HAVAK, label: getPriceLabel('O_HAVAK'), value: costsPayload.O_HAVAK },
              { condition: watchO_HBSAK, label: getPriceLabel('O_HBSAK'), value: costsPayload.O_HBSAK },
              { condition: watchO_MASERN, label: getPriceLabel('O_MASERN'), value: costsPayload.O_MASERN },
              { condition: watchO_MUMPS, label: getPriceLabel('O_MUMPS'), value: costsPayload.O_MUMPS },
              { condition: watchO_ROET, label: getPriceLabel('O_ROET'), value: costsPayload.O_ROET },
              { condition: watchO_VZV, label: getPriceLabel('O_VZV'), value: costsPayload.O_VZV },
              { condition: watchCovidABTest, label: getPriceLabel('covidABTest'), value: costsPayload.covidABTest },
              { condition: watchO_DROG, label: getPriceLabel('O_DROG'), value: costsPayload.O_DROG },
              { condition: watchO_CDT, label: getPriceLabel('O_CDT'), value: costsPayload.O_CDT },
              { condition: watchO_CDTPRO, label: getPriceLabel('O_CDTPRO'), value: costsPayload.O_CDTPRO },
              { condition: watchO_HIV, label: getPriceLabel('O_HIV'), value: costsPayload.O_HIV },
              { condition: watchO_HEPC, label: getPriceLabel('O_HEPC'), value: costsPayload.O_HEPC },
              { condition: watchO_DAO, label: getPriceLabel('O_DAO'), value: costsPayload.O_DAO },
              { condition: watchO_OMEGA, label: getPriceLabel('O_OMEGA'), value: costsPayload.O_OMEGA },
              // { condition: watchO_RESPI, label: getPriceLabel('O_RESPI'), value: costsPayload.O_RESPI },
              { condition: watchO_NAV, label: getPriceLabel('O_NAV'), value: costsPayload.O_NAV },
              { condition: watchO_KV, label: getPriceLabel('O_KV'), value: costsPayload.O_KV },
              { condition: watchO_CAV, label: getPriceLabel('O_CAV'), value: costsPayload.O_CAV },
              { condition: watchO_MGV, label: getPriceLabel('O_MGV'), value: costsPayload.O_MGV },
              { condition: watchO_FEV, label: getPriceLabel('O_FEV'), value: costsPayload.O_FEV },
              { condition: watchO_SEV, label: getPriceLabel('O_SEV'), value: costsPayload.O_SEV },
              { condition: watchO_ZNV, label: getPriceLabel('O_ZNV'), value: costsPayload.O_ZNV },
              { condition: watchO_CUV, label: getPriceLabel('O_CUV'), value: costsPayload.O_CUV },
              { condition: watchO_VMULTIK, label: getPriceLabel('O_VMULTIK'), value: costsPayload.O_VMULTIK },
              { condition: watchO_VMULTIP, label: getPriceLabel('O_VMULTIP'), value: costsPayload.O_VMULTIP },
              { condition: watchO_SMULTIK, label: getPriceLabel('O_SMULTIK'), value: costsPayload.O_SMULTIK },
              { condition: watchO_SMULTIP, label: getPriceLabel('O_SMULTIP'), value: costsPayload.O_SMULTIP },
              { condition: watchO_HMULTIK, label: getPriceLabel('O_HMULTIK'), value: costsPayload.O_HMULTIK },
              { condition: watchO_HMULTIP, label: getPriceLabel('O_HMULTIP'), value: costsPayload.O_HMULTIP },
              { condition: watchO_VITA, label: getPriceLabel('O_VITA'), value: costsPayload.O_VITA },
              { condition: watchO_VITB1, label: getPriceLabel('O_VITB1'), value: costsPayload.O_VITB1 },
              { condition: watchO_VITB2, label: getPriceLabel('O_VITB2'), value: costsPayload.O_VITB2 },
              { condition: watchO_VITB3, label: getPriceLabel('O_VITB3'), value: costsPayload.O_VITB3 },
              { condition: watchO_VB6, label: getPriceLabel('O_VB6'), value: costsPayload.O_VB6 },
              { condition: watchO_FOL, label: getPriceLabel('O_FOL'), value: costsPayload.O_FOL },
              { condition: watchO_HOLOTC, label: getPriceLabel('O_HOLOTC'), value: costsPayload.O_HOLOTC },
              { condition: watchO_VB12, label: getPriceLabel('O_VB12'), value: costsPayload.O_VB12 },
              { condition: watchO_VITC, label: getPriceLabel('O_VITC'), value: costsPayload.O_VITC },
              { condition: watchO_VITD, label: getPriceLabel('O_VITD'), value: costsPayload.O_VITD },
              { condition: watchO_VD2, label: getPriceLabel('O_VD2'), value: costsPayload.O_VD2 },
              { condition: watchO_VITE, label: getPriceLabel('O_VITE'), value: costsPayload.O_VITE },
              { condition: watchO_VITK1, label: getPriceLabel('O_VITK1'), value: costsPayload.O_VITK1 },
              { condition: watchO_VITK2, label: getPriceLabel('O_VITK2'), value: costsPayload.O_VITK2 },
              { condition: watchO_UBI, label: getPriceLabel('O_UBI'), value: costsPayload.O_UBI },
              { condition: watchBloodDraw, label: t('bookingFormBloodDraw'), value: costsPayload.bloodDraw },
              { condition: watchInvoice, label: t('bookingFormInvoiceExtrasHeader'), value: costsPayload.invoice },
            ].map(({ condition, label, value }, index) =>
                condition && (
                  <p key={index} className={style.rowSpaceBetween}>
                        <span className={style.priceLineLabel}>
                          {label.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {i === 1 ? <span className={style.priceLineSub}>{line}</span> : line}
                              {i < 1 && <br />}
                            </React.Fragment>
                          ))}
                        </span>
                    <Price value={value} symbol={costs.currencySymbol} />
                  </p>
                )
            )}

            <div className={style.hline} />
            <p className={style.rowSpaceBetween}>
              <span className={style.totalPrice}>{t('bookingFormTotal')}</span>
              <Price value={totalPrice} className={style.totalPrice} symbol={costs.currencySymbol} />
            </p>
          </div>

          {preSelection ? (
            <Button
              type='primary'
              disabled={
                formState.isSubmitting ||
                totalPrice + costs.discount < minTestCost ||
                errors.category1Validation
              }
              style={{ marginTop: '3rem' }}
              htmlType='button'
              onClick={handleAppointmentClick}
            >
              {t('bookingFormProceedToTimeslot')}
            </Button>
          ) : (
            <Button
              type='primary'
              disabled={
                formState.isSubmitting ||
                !formState.isValid ||
                totalPrice + costs.discount < minTestCost ||
                errors.category1Validation
              }
              style={{ marginTop: '3rem' }}
              htmlType='submit'
            >
              {t('bookingFormProceedToPayment')}
            </Button>
          )}

          <p className={style.secondaryLine}>{t('additionalLineOne')}</p>
          <p className={style.secondaryLine}>{t('additionalLineTwo')}</p>
        </div>

      </div>
      <HomePageDialog handleDialogClose={handleHomeDialogClose} showDialog={state.showHomePageDialog} />
    </div>
  );
};
// TODO consider moving HomePageDialog to BookingProductSelectionPageContainer instead
